/**
 * Permission keys are the single vocabulary used by the server-side authorization
 * guard. Roles are rows in the database that bundle these keys, so new roles can be
 * created administratively without a code change.
 */
export const PERMISSIONS = {
  WORK_ORDER_VIEW: 'work_order.view',
  WORK_ORDER_CREATE: 'work_order.create',
  WORK_ORDER_EDIT: 'work_order.edit',
  WORK_ORDER_ASSIGN: 'work_order.assign',
  WORK_ORDER_CLOSE: 'work_order.close',
  WORK_ORDER_CANCEL: 'work_order.cancel',
  WORK_ORDER_HOLD: 'work_order.hold',
  WORK_ORDER_PURGE: 'work_order.purge',

  INVESTIGATION_MANAGE: 'investigation.manage',

  PROPOSAL_VIEW: 'proposal.view',
  PROPOSAL_MANAGE: 'proposal.manage',
  PROPOSAL_SUBMIT: 'proposal.submit',
  PROPOSAL_APPROVE: 'proposal.approve',

  /** Record the PR/PO or P-card for an approved work order (administrative). */
  PROCUREMENT_MANAGE: 'procurement.manage',
  EXECUTION_MANAGE: 'execution.manage',
  COMPLETION_MANAGE: 'completion.manage',

  COMMENT_CREATE_CUSTOMER: 'comment.create_customer_visible',
  COMMENT_CREATE_INTERNAL: 'comment.create_internal',

  ATTACHMENT_UPLOAD: 'attachment.upload',
  ATTACHMENT_UPLOAD_INTERNAL: 'attachment.upload_internal',

  /** The single gate for every internal-only field, note, cost, and attachment. */
  INTERNAL_VIEW: 'internal.view',

  REPORT_VIEW: 'report.view',
  REPORT_FINANCIAL_VIEW: 'report.financial_view',
  AUDIT_VIEW: 'audit.view',

  ADMIN_USER_MANAGE: 'admin.user_manage',
  ADMIN_ROLE_MANAGE: 'admin.role_manage',
  ADMIN_FACILITY_MANAGE: 'admin.facility_manage',
  ADMIN_TASK_ORDER_MANAGE: 'admin.task_order_manage',
  ADMIN_MARKUP_CONFIGURE: 'admin.markup_configure',
  ADMIN_CATEGORY_MANAGE: 'admin.category_manage',
  ADMIN_WORKFLOW_CONFIGURE: 'admin.workflow_configure',

  /** Employee document packages: creation, assignment, and release to USPS. */
  EMPLOYEE_DOCS_MANAGE: 'employee_docs.manage',
  /** Item-level review of an employee's submitted documents. */
  EMPLOYEE_DOCS_REVIEW: 'employee_docs.review',
  /** USPS Contracting Officers: the packages VGS has released to USPS, nothing earlier. */
  EMPLOYEE_DOCS_VIEW_RELEASED: 'employee_docs.view_released',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: readonly Permission[] = Object.values(PERMISSIONS);

export const ORG_TYPES = ['contractor', 'customer'] as const;
export type OrgType = (typeof ORG_TYPES)[number];

/** Role codes seeded by migration. Additional roles are data, not code. */
export const ROLE_CODES = {
  VGS_SYSTEM_ADMINISTRATOR: 'vgs_system_administrator',
  VGS_PROGRAM_MANAGER: 'vgs_program_manager',
  VGS_FACILITY_MANAGER: 'vgs_facility_manager',
  VGS_STAFF: 'vgs_staff',
  /** Reviews employee document packages only; no work-order permissions. */
  VGS_DOCUMENT_REVIEWER: 'vgs_document_reviewer',
  CUSTOMER_USER: 'customer_user',
  /** Contracting Officer's Representative: approves within the COR dollar limit. */
  CUSTOMER_COR: 'customer_cor',
  /** Contracting Officer: approves above the COR limit. */
  CUSTOMER_CO: 'customer_co',
  AUDITOR: 'auditor',
} as const;

export type RoleCode = (typeof ROLE_CODES)[keyof typeof ROLE_CODES];

export const VISIBILITY = {
  CUSTOMER_VISIBLE: 'customer_visible',
  INTERNAL: 'internal',
} as const;

export type Visibility = (typeof VISIBILITY)[keyof typeof VISIBILITY];
